import { formatDateTimeIn, formatMoneyIn, toLocale } from '@zuruflex/i18n';
import type { Booking } from '@zuruflex/types';
import { Badge, EmptyState, ErrorState, Money, Skeleton } from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { promoLabel } from '../lib/promotions.js';

interface BookingRow extends Booking {
  customer?: { fullName: string | null; phone: string } | null;
}

const STATUS_VARIANT = {
  PENDING: 'warning',
  AWAITING_PAYMENT: 'info',
  CONFIRMED: 'info',
  ACTIVE: 'success',
  COMPLETED: 'default',
  CANCELLED: 'outline',
} as const;

/**
 * This car's hires — the answer to "has this one earned anything".
 *
 * Filtered by the API rather than here: a partner with twenty-four cars and
 * a season behind them would otherwise pull every booking they have ever
 * had to show the four that belong to this Swift.
 */
export function CarBookings({ vehicleId }: { vehicleId: string }) {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['vehicle-bookings', vehicleId],
    queryFn: () => api.listBookings({ vehicleId, perPage: 100 }),
  });

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending) {
    return (
      <div className="flex flex-col gap-3" aria-hidden>
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-20 rounded-card" />
        ))}
      </div>
    );
  }

  /* Soonest first while it is still ahead, most recent first once it is
     behind — the same order the bookings page uses, for the same reason. */
  const now = Date.now();
  const hires = [...((data?.items ?? []) as BookingRow[])].sort((a, b) => {
    const left = new Date(a.pickupAt).getTime();
    const right = new Date(b.pickupAt).getTime();
    const ahead = (value: number) => (value >= now ? 0 : 1);
    return ahead(left) - ahead(right) || (ahead(left) === 0 ? left - right : right - left);
  });

  if (hires.length === 0) {
    return <EmptyState title={t('carDetail.noHires')} description={t('carDetail.noHiresBody')} />;
  }

  return (
    <ul className="flex flex-col gap-2">
      {hires.map((booking) => (
        <li key={booking.id}>
          <Link
            to={`/bookings?ref=${encodeURIComponent(booking.reference)}`}
            className="flex flex-wrap items-center gap-3 rounded-field border border-border-default px-3 py-2.5 transition-colors hover:border-border-strong hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">
                  {formatDateTimeIn(booking.pickupAt, locale)} →{' '}
                  {formatDateTimeIn(booking.dropoffAt, locale)}
                </span>
                <Badge variant={STATUS_VARIANT[booking.status]}>
                  {t(`common:bookingStatus.${booking.status}`)}
                </Badge>
              </p>
              <p className="mt-0.5 text-xs text-text-muted">
                <span className="font-mono">{booking.reference}</span>
                {booking.customer ? (
                  <>
                    {' · '}
                    {booking.customer.fullName ?? t('bookings.unnamedCustomer')}
                  </>
                ) : null}
                {/* Which promo, if any: the share beside it is already after it. */}
                {booking.promoDiscount && booking.promoFunder ? (
                  <>
                    {' · '}
                    {promoLabel(booking.promoCode ?? null, t)} ·{' '}
                    {t(
                      booking.promoFunder === 'PARTNER'
                        ? 'bookings.promoYours'
                        : 'bookings.promoZuruflex',
                      {
                        amount: formatMoneyIn(booking.promoDiscount, booking.currency, locale),
                      },
                    )}
                  </>
                ) : null}
              </p>
            </div>

            <div className="shrink-0 text-right">
              <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
                {t('bookings.yourShare')}
              </p>
              <Money amount={booking.partnerEarnings ?? 0} currency={booking.currency} />
            </div>

            <ChevronRight className="size-4 shrink-0 text-text-muted" aria-hidden />
          </Link>
        </li>
      ))}
    </ul>
  );
}
